/**
 * Note Metadata Component
 *
 * Displays encounter information in the note view.
 *
 * Features:
 * - Encounter date, start time, and duration from encounter data (with fallback to transcription data)
 * - Editable encounter time when data comes from encounter (not transcription)
 * - Horizontal layout with dividers between items
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateEncounterMutation, type Encounter } from '@features/encounters';
import { useEventTracking } from '@services/statsig';
import {
  Alarm,
  AssignmentIndOutlined,
  CalendarToday,
  Check,
  Close as CloseIcon,
  Person,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

import { NoteDataType, Patient } from '../types/types';
import { formatDate, formatDuration } from '../utils/utils';

// Quick duration options (matching appointment components)
const QUICK_DURATIONS = [
  { label: '30m', minutes: 30 },
  { label: '45m', minutes: 45 },
  { label: '1h', minutes: 60 },
];

/**
 * Metadata item display component
 * Defined outside main component to prevent recreation on each render
 */
const MetadataItem: React.FC<{
  icon: React.ReactNode;
  text: string;
  tooltip: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  clickable?: boolean;
}> = ({ icon, text, tooltip, onClick, clickable = false }) => (
  <Tooltip title={tooltip} arrow>
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        my: 0.5,
        mr: 1,
        minWidth: 0,
        color: 'newDesignColors.eggplantNeutral700',
        ...(clickable && {
          cursor: 'pointer',
          borderRadius: 1,
          px: 1,
          py: 0.5,
          mx: -1,
          my: 0,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }),
      }}
    >
      {React.cloneElement(icon as React.ReactElement, {
        sx: { mr: 0.5, flexShrink: 0 },
        fontSize: 'small',
        color: 'newDesignColors.eggplantNeutral700',
      })}
      <Typography
        variant="subtitle1"
        noWrap
        sx={{
          flexGrow: 1,
          minWidth: 0,
          color: 'newDesignColors.eggplantNeutral700',
        }}
      >
        {text}
      </Typography>
    </Box>
  </Tooltip>
);

interface NoteMetadataProps {
  noteData: NoteDataType;
  patient?: Patient;
  /** Note ID for tracking (optional for backward compatibility) */
  noteId?: string;
  /** Encounter data with start_time, end_time, and duration (optional) */
  encounterData?: Encounter;
  /** Whether encounter time editing is allowed (optional, defaults to false) */
  editable?: boolean;
}

export const NoteMetadata: React.FC<NoteMetadataProps> = ({
  noteData,
  patient,
  noteId,
  encounterData,
  editable = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { note } = useEventTracking();

  // State for encounter time editing
  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState<string>('');
  const [isCustomDuration, setIsCustomDuration] = useState(false);

  // Ref for the date/time metadata item (used as popover anchor)
  const dateTimeRef = useRef<HTMLDivElement>(null);

  // RTK Query mutation for updating encounter
  const [updateEncounter] = useUpdateEncounterMutation();

  // Extract encounter_id
  const encounterId =
    encounterData?.encounter_id ||
    (noteData as any)?.encounter?.encounter_id ||
    (noteData as any)?.encounter_id;

  // Determine if time data comes from encounter (editable) or transcription (read-only)
  const hasEncounterTimeData = Boolean(
    encounterData?.start_time || encounterData?.appointment?.start_time,
  );

  // Determine start time with fallback chain:
  // 1. encounterData.start_time
  // 2. encounterData.appointment.start_time
  // 3. noteData.transcription_start_timestamp
  // 4. noteData.time_uploaded
  let startTimeEpoch: number | null = null;
  if (encounterData?.start_time) {
    startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
  } else if (encounterData?.appointment?.start_time) {
    startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
  } else if (noteData.transcription_start_timestamp) {
    startTimeEpoch = noteData.transcription_start_timestamp;
  } else if (noteData.time_uploaded) {
    startTimeEpoch = noteData.time_uploaded;
  }

  // Calculate duration with fallback chain:
  // 1. Calculate from encounter start_time and end_time
  // 2. Use appointment duration (if encounter times not available)
  // 3. Fall back to transcription_length_seconds
  let durationSeconds: number | null = null;
  if (encounterData?.start_time && encounterData?.end_time) {
    const start = new Date(encounterData.start_time).getTime();
    const end = new Date(encounterData.end_time).getTime();
    durationSeconds = Math.floor((end - start) / 1000);
  } else if (encounterData?.appointment?.duration) {
    // Appointment duration is in minutes, convert to seconds
    durationSeconds = encounterData.appointment.duration * 60;
  } else if (noteData.transcription_length_seconds) {
    durationSeconds = noteData.transcription_length_seconds;
  }

  // Initialize time editor state when popover opens
  useEffect(() => {
    if (timeAnchorEl && encounterData) {
      // Get the start time from encounter data
      const encounterStartTime = encounterData.start_time || encounterData.appointment?.start_time;
      if (encounterStartTime) {
        const startDate = new Date(encounterStartTime);
        setSelectedDate(format(startDate, 'yyyy-MM-dd'));
        setStartTime(format(startDate, 'HH:mm'));
      }

      // Calculate duration in minutes
      let duration = 0;
      if (encounterData.start_time && encounterData.end_time) {
        const start = new Date(encounterData.start_time).getTime();
        const end = new Date(encounterData.end_time).getTime();
        duration = Math.floor((end - start) / (1000 * 60));
      } else if (encounterData.appointment?.duration) {
        duration = encounterData.appointment.duration;
      }

      // Check if duration matches a quick preset
      const isPresetDuration = QUICK_DURATIONS.some(preset => preset.minutes === duration);
      setIsCustomDuration(!isPresetDuration && duration > 0);
      setDurationMinutes(duration > 0 ? String(duration) : '');
    }
  }, [timeAnchorEl, encounterData]);

  /**
   * Handle saving encounter time changes
   */
  const handleSaveEncounterTime = useCallback(() => {
    if (!encounterId || !selectedDate || !startTime) return;

    const startDateTime = new Date(`${selectedDate}T${startTime}`);
    const duration = parseInt(durationMinutes, 10) || 0;
    const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000);

    // Track event
    if (noteId) {
      note.encounterTimeSaved({
        note_id: noteId,
        encounter_id: encounterId,
        duration_minutes: duration,
        is_custom_duration: isCustomDuration,
      });
    }

    // Close popover immediately (optimistic)
    setTimeAnchorEl(null);

    // Fire mutation
    updateEncounter({
      encounterId,
      encounterUpdate: {
        start_time: startDateTime.toISOString(),
        end_time: duration > 0 ? endDateTime.toISOString() : null,
      },
    })
      .unwrap()
      .catch(error => {
        console.error('Error updating encounter time:', error);
        enqueueSnackbar('Failed to update encounter time', { variant: 'error' });
      });
  }, [
    encounterId,
    selectedDate,
    startTime,
    durationMinutes,
    isCustomDuration,
    noteId,
    note,
    updateEncounter,
    enqueueSnackbar,
  ]);

  /**
   * Handle opening time editor popover
   */
  const handleOpenTimeEditor = useCallback(() => {
    if (noteId) {
      note.encounterTimeEditorOpened({
        note_id: noteId,
        encounter_id: encounterId,
      });
    }
    // Use the ref instead of event.currentTarget to avoid Tooltip interference
    if (dateTimeRef.current) {
      setTimeAnchorEl(dateTimeRef.current);
    }
  }, [noteId, encounterId, note]);

  /**
   * Handle closing time editor popover
   */
  const handleCloseTimeEditor = useCallback(() => {
    setTimeAnchorEl(null);
  }, []);

  /**
   * Handle quick duration selection
   */
  const handleQuickDurationSelect = useCallback((minutes: number) => {
    setDurationMinutes(String(minutes));
    setIsCustomDuration(false);
  }, []);

  /**
   * Handle custom duration selection
   */
  const handleCustomDurationSelect = useCallback(() => {
    setIsCustomDuration(true);
  }, []);

  const isTimeEditorOpen = Boolean(timeAnchorEl);
  const canSaveTime = selectedDate && startTime;
  const selectedDuration = parseInt(durationMinutes, 10) || 0;

  // Determine if encounter time is editable (data comes from encounter, not transcription)
  const isTimeEditable = editable && hasEncounterTimeData && Boolean(encounterId);

  // Format date/time text for display
  let dateTimeText = '';
  if (startTimeEpoch) {
    const dateText = formatDate(startTimeEpoch, 'datelong');
    const timeText = durationSeconds
      ? `${formatDate(startTimeEpoch, 'time')} – ${formatDate(startTimeEpoch + durationSeconds, 'time')}`
      : formatDate(startTimeEpoch, 'time');
    dateTimeText = `${dateText}, ${timeText}`;
  }

  // Build metadata items (excluding date/time which is rendered separately with a ref)
  const metadataItems = [
    !noteData.note_title &&
      patient?.first_name &&
      patient?.last_name && {
        icon: <Person />,
        text: `${patient.first_name} ${patient.last_name}`,
        tooltip: 'Patient',
      },
    noteData.completion_status == 'completed' &&
      noteData.completion_timestamp && {
        icon: <Check />,
        text: formatDate(noteData.completion_timestamp, 'both'),
        tooltip: noteData.is_signed ? 'Note Signed' : 'Note marked as complete',
      },
    {
      icon: <AssignmentIndOutlined />,
      text: noteData.issuing_user_name,
      tooltip: 'Provider',
    },
  ].filter(Boolean) as Array<{
    icon: React.ReactNode;
    text: string;
    tooltip: string;
  }>;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          '& > *:not(:last-child)::after': {
            content: '""',
            display: 'inline-block',
            width: '2px',
            height: '24px',
            backgroundColor: 'divider',
            marginLeft: 1,
          },
        }}
      >
        {metadataItems.map((item, index) => (
          <MetadataItem key={index} icon={item.icon} text={item.text} tooltip={item.tooltip} />
        ))}

        {/* Date/Time item - rendered separately with ref for popover anchoring */}
        {dateTimeText && (
          <Tooltip
            title={
              isTimeEditable ? 'Click to edit encounter date and time' : 'Encounter Date and Time'
            }
            arrow
          >
            <Box
              ref={dateTimeRef}
              onClick={isTimeEditable ? handleOpenTimeEditor : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                my: 0.5,
                mr: 1,
                minWidth: 0,
                color: 'newDesignColors.eggplantNeutral700',
                ...(isTimeEditable && {
                  cursor: 'pointer',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  mx: -1,
                  my: 0,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }),
              }}
            >
              <CalendarToday
                sx={{ mr: 0.5, flexShrink: 0 }}
                fontSize="small"
                htmlColor="newDesignColors.eggplantNeutral700"
              />
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  color: 'newDesignColors.eggplantNeutral700',
                }}
              >
                {dateTimeText}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Duration item */}
        {durationSeconds && (
          <MetadataItem
            icon={<Alarm />}
            text={formatDuration(durationSeconds)}
            tooltip="Encounter Duration"
          />
        )}
      </Box>

      {/* Encounter Time Editor Popover */}
      <Popover
        open={isTimeEditorOpen}
        anchorEl={timeAnchorEl}
        onClose={handleCloseTimeEditor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography variant="h6">Encounter Time</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  When did this encounter take place?
                </Typography>
              </Box>
              <IconButton onClick={handleCloseTimeEditor} size="small" sx={{ ml: 2 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <TextField
              type="date"
              label="Date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && canSaveTime) {
                  e.preventDefault();
                  handleSaveEncounterTime();
                }
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="time"
              label="Start Time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && canSaveTime) {
                  e.preventDefault();
                  handleSaveEncounterTime();
                }
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={!selectedDate}
            />

            {/* Quick duration chips */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Duration:
              </Typography>
              {QUICK_DURATIONS.map(duration => (
                <Chip
                  key={duration.minutes}
                  label={duration.label}
                  onClick={() => handleQuickDurationSelect(duration.minutes)}
                  color={
                    selectedDuration === duration.minutes && !isCustomDuration
                      ? 'primary'
                      : 'default'
                  }
                  variant={
                    selectedDuration === duration.minutes && !isCustomDuration
                      ? 'filled'
                      : 'outlined'
                  }
                  size="small"
                />
              ))}
              <Chip
                label="Other"
                onClick={handleCustomDurationSelect}
                color={isCustomDuration ? 'primary' : 'default'}
                variant={isCustomDuration ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>

            {/* Custom duration input - only show when "Other" is selected */}
            {isCustomDuration && (
              <TextField
                type="number"
                label="Duration (min)"
                value={durationMinutes}
                onChange={e => setDurationMinutes(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && canSaveTime) {
                    e.preventDefault();
                    handleSaveEncounterTime();
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={!selectedDate}
                inputProps={{ min: 0 }}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              <Button
                variant="text"
                onClick={handleCloseTimeEditor}
                sx={{ color: 'text.secondary' }}
              >
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveEncounterTime} disabled={!canSaveTime}>
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
